import React, { useState } from 'react';
import { generateDichotomyAnalysis } from '../services/geminiService';
import { DichotomyAnalysis } from '../types';
import { Scale, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { useUser } from '../context/UserContext';

const StoicControl: React.FC = () => {
  const [worry, setWorry] = useState('');
  const [analysis, setAnalysis] = useState<DichotomyAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const { addXp } = useUser();

  const handleAnalyze = async () => {
    if (!worry.trim()) return;
    setLoading(true);
    setAnalysis(null);
    try {
      const data = await generateDichotomyAnalysis(worry);
      setAnalysis(data);
      addXp(40, 'Practiced Dichotomy of Control');
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-stone-100 text-stone-600 rounded-full mb-4">
          <Scale size={32} />
        </div>
        <h1 className="text-3xl font-serif font-bold text-nunchi-900 mb-2">Dichotomy of Control</h1>
        <p className="text-nunchi-600">
          "Some things are up to us, some things are not." <br/>
          Identify the line to find tranquility.
        </p>
      </header>

      {!analysis && !loading && (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-nunchi-200">
            <label className="block text-sm font-bold text-nunchi-500 uppercase tracking-wider mb-3">
                What is disturbing your peace?
            </label>
            <textarea
              value={worry}
              onChange={(e) => setWorry(e.target.value)}
              placeholder="e.g., I'm worried about the upcoming presentation, Traffic is making me late, People think I'm boring..."
              className="w-full h-32 bg-nunchi-50 rounded-xl p-4 border-none focus:ring-2 focus:ring-stone-200 outline-none resize-none text-nunchi-800 placeholder:text-nunchi-300 mb-4"
            />
            <button
              onClick={handleAnalyze}
              disabled={!worry.trim()}
              className="w-full bg-stone-600 text-white py-4 rounded-xl font-bold hover:bg-stone-700 transition-all flex justify-center items-center gap-2 disabled:opacity-50"
            >
              <Scale size={18} /> Separate the Elements
            </button>
        </div>
      )}

      {loading && (
        <div className="text-center py-20">
             <div className="w-16 h-16 border-4 border-stone-200 border-t-stone-600 rounded-full animate-spin mx-auto mb-4"></div>
             <p className="font-serif italic text-stone-600">Sorting the internal from the external...</p>
        </div>
      )}

      {analysis && (
        <div className="animate-fadeIn space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* In Control */}
                <div className="bg-emerald-50/80 p-6 rounded-xl border border-emerald-100">
                    <h3 className="text-emerald-700 font-bold uppercase tracking-wider text-xs mb-3 flex items-center gap-2">
                        <CheckCircle size={14} /> Up to You
                    </h3>
                    <ul className="space-y-2">
                        {analysis.inControl.map((item, idx) => (
                            <li key={idx} className="text-emerald-900 text-sm font-medium flex items-start gap-2">
                                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></span>
                                {item}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Not In Control */}
                <div className="bg-stone-100 p-6 rounded-xl border border-stone-200 opacity-80">
                    <h3 className="text-stone-500 font-bold uppercase tracking-wider text-xs mb-3 flex items-center gap-2">
                        <XCircle size={14} /> Not Up to You
                    </h3>
                    <ul className="space-y-2">
                        {analysis.notInControl.map((item, idx) => (
                            <li key={idx} className="text-stone-600 text-sm italic flex items-start gap-2 line-through decoration-stone-300">
                                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-stone-300 flex-shrink-0"></span>
                                {item}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="bg-white border border-nunchi-200 rounded-2xl p-8 shadow-sm">
                 <h3 className="text-stone-400 uppercase tracking-widest text-xs font-bold mb-3">Stoic Advice</h3>
                 <p className="text-lg leading-relaxed font-serif text-nunchi-800 mb-6">
                     "{analysis.advice}"
                 </p>
                 <div className="bg-stone-50 p-4 rounded-lg text-center border border-stone-100">
                     <p className="text-xs text-stone-400 uppercase font-bold mb-1">Your Mantra</p>
                     <p className="text-lg font-bold text-stone-700">"{analysis.mantra}"</p>
                 </div>
            </div>

            <div className="text-center">
                <button 
                  onClick={() => { setAnalysis(null); setWorry(''); }}
                  className="text-stone-500 hover:text-stone-800 font-bold flex items-center gap-2 mx-auto"
                >
                    <RefreshCw size={16} /> Analyze Another Worry
                </button>
            </div>
        </div>
      )}
    </div>
  );
};

export default StoicControl;